/**
 * Export Dialog Component
 * Tally Prime-style export dialog with ALT+E shortcut
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { FileSpreadsheet, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

const ExportDialog = ({
  open,
  onOpenChange,
  onExport,
  title = 'Export',
  hasFilters = false,
  filterDescription = '',
  totalRecords = 0,
  filteredRecords = 0,
}) => {
  const [format, setFormat] = useState('excel');
  const [scope, setScope] = useState('current');
  const [exporting, setExporting] = useState(false);

  // Handle keyboard shortcuts within dialog
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e) => {
      // E for Excel
      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        setFormat('excel');
      }
      // P for PDF
      if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        setFormat('pdf');
      }
      // C for Current View
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        setScope('current');
      }
      // A for All Records
      if (e.key === 'a' || e.key === 'A') {
        e.preventDefault();
        setScope('all');
      }
      // Enter to confirm
      if (e.key === 'Enter') {
        e.preventDefault();
        handleExport();
      }
      // Escape to close
      if (e.key === 'Escape') {
        e.preventDefault();
        onOpenChange(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, format, scope]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await onExport(format, scope);
      if (result.success) {
        toast.success(result.message);
        onOpenChange(false);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[380px] p-0 gap-0 bg-white" data-testid="export-dialog">
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b bg-maroon-50">
          <DialogTitle className="text-base font-semibold text-maroon-800 flex items-center gap-2">
            <Download className="h-4 w-4" />
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Choose export format and scope
          </DialogDescription>
        </DialogHeader>

        <div className="p-4 space-y-4">
          {/* Format Selection */}
          <div>
            <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">
              Export Format
            </Label>
            <RadioGroup value={format} onValueChange={setFormat} className="flex gap-2">
              <label
                className={`flex-1 flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  format === 'excel'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <RadioGroupItem value="excel" id="excel" className="sr-only" />
                <FileSpreadsheet className={`h-5 w-5 ${format === 'excel' ? 'text-emerald-600' : 'text-gray-400'}`} />
                <div>
                  <div className={`text-sm font-medium ${format === 'excel' ? 'text-emerald-700' : 'text-gray-700'}`}>
                    Excel
                  </div>
                  <div className="text-xs text-gray-500">.xlsx</div>
                </div>
                <span className="ml-auto text-xs text-gray-400 font-mono">[E]</span>
              </label>

              <label
                className={`flex-1 flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  format === 'pdf'
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <RadioGroupItem value="pdf" id="pdf" className="sr-only" />
                <FileText className={`h-5 w-5 ${format === 'pdf' ? 'text-red-600' : 'text-gray-400'}`} />
                <div>
                  <div className={`text-sm font-medium ${format === 'pdf' ? 'text-red-700' : 'text-gray-700'}`}>
                    PDF
                  </div>
                  <div className="text-xs text-gray-500">.pdf</div>
                </div>
                <span className="ml-auto text-xs text-gray-400 font-mono">[P]</span>
              </label>
            </RadioGroup>
          </div>

          {/* Scope Selection */}
          <div>
            <Label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">
              Export Scope
            </Label>
            <RadioGroup value={scope} onValueChange={setScope} className="space-y-2">
              <label
                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  scope === 'current'
                    ? 'border-maroon-500 bg-maroon-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <RadioGroupItem value="current" id="current" className="sr-only" />
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  scope === 'current' ? 'border-maroon-500' : 'border-gray-300'
                }`}>
                  {scope === 'current' && <div className="w-2 h-2 rounded-full bg-maroon-500" />}
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-medium ${scope === 'current' ? 'text-maroon-700' : 'text-gray-700'}`}>
                    Current View
                  </div>
                  <div className="text-xs text-gray-500">
                    {hasFilters ? `${filteredRecords} records (filtered)` : `${totalRecords} records`}
                  </div>
                </div>
                <span className="text-xs text-gray-400 font-mono">[C]</span>
              </label>

              <label
                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  scope === 'all'
                    ? 'border-maroon-500 bg-maroon-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <RadioGroupItem value="all" id="all" className="sr-only" />
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  scope === 'all' ? 'border-maroon-500' : 'border-gray-300'
                }`}>
                  {scope === 'all' && <div className="w-2 h-2 rounded-full bg-maroon-500" />}
                </div>
                <div className="flex-1">
                  <div className={`text-sm font-medium ${scope === 'all' ? 'text-maroon-700' : 'text-gray-700'}`}>
                    All Records
                  </div>
                  <div className="text-xs text-gray-500">{totalRecords} records total</div>
                </div>
                <span className="text-xs text-gray-400 font-mono">[A]</span>
              </label>
            </RadioGroup>
          </div>

          {/* Filter info */}
          {hasFilters && filterDescription && scope === 'current' && (
            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
              <span className="font-medium">Applied filters:</span> {filterDescription}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            Press <span className="font-mono bg-gray-200 px-1 rounded">Enter</span> to export
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8"
              data-testid="export-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleExport}
              disabled={exporting}
              className="h-8 bg-maroon-600 hover:bg-maroon-700 text-white"
              data-testid="export-confirm"
            >
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportDialog;
